import { useState } from 'react';

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  createdAt: string;
  lastDelivery: string | null;
  lastStatus: 'success' | 'failed' | null;
}

const mockWebhooks: WebhookEndpoint[] = [
  {
    id: 'wh_001',
    name: 'CRM — Novo Contato',
    url: 'https://hooks.zapier.com/hooks/catch/123456/abc123/',
    events: ['contact.created', 'contact.updated'],
    enabled: true,
    createdAt: '2026-06-15',
    lastDelivery: '2026-08-04T14:22:00Z',
    lastStatus: 'success',
  },
  {
    id: 'wh_002',
    name: 'Slack — Campanha Concluída',
    url: 'https://hooks.slack.com/services/T01/B02/xxxxx',
    events: ['campaign.completed'],
    enabled: true,
    createdAt: '2026-07-01',
    lastDelivery: '2026-08-03T18:45:00Z',
    lastStatus: 'success',
  },
  {
    id: 'wh_003',
    name: 'n8n — Processar Mensagens',
    url: 'https://n8n.minhaempresa.com/webhook/wzp-messages',
    events: ['message.received', 'message.sent'],
    enabled: true,
    createdAt: '2026-07-20',
    lastDelivery: '2026-08-04T15:30:00Z',
    lastStatus: 'success',
  },
  {
    id: 'wh_004',
    name: 'Planilha Google — Backup',
    url: 'https://script.google.com/macros/s/xxxxx/exec',
    events: ['contact.created'],
    enabled: false,
    createdAt: '2026-05-10',
    lastDelivery: '2026-07-28T09:00:00Z',
    lastStatus: 'failed',
  },
];

const availableEvents = [
  { key: 'message.received', label: 'Mensagem Recebida', icon: 'ri-chat-1-line' },
  { key: 'message.sent', label: 'Mensagem Enviada', icon: 'ri-send-plane-line' },
  { key: 'contact.created', label: 'Contato Criado', icon: 'ri-user-add-line' },
  { key: 'contact.updated', label: 'Contato Atualizado', icon: 'ri-user-settings-line' },
  { key: 'campaign.started', label: 'Campanha Iniciada', icon: 'ri-megaphone-line' },
  { key: 'campaign.completed', label: 'Campanha Concluída', icon: 'ri-check-double-line' },
  { key: 'campaign.paused', label: 'Campanha Pausada', icon: 'ri-pause-circle-line' },
  { key: 'payment.approved', label: 'Pagamento Aprovado', icon: 'ri-bank-card-line' },
];

export default function IntegrationsPage() {
  const [activeTab, setActiveTab] = useState<'zapier' | 'n8n' | 'webhooks'>('webhooks');
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>(mockWebhooks);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTestResult, setShowTestResult] = useState<{ id: string; status: 'success' | 'failed' } | null>(null);

  // New webhook form
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newEvents, setNewEvents] = useState<string[]>([]);

  const toggleEvent = (eventKey: string) => {
    setNewEvents((prev) =>
      prev.includes(eventKey) ? prev.filter((e) => e !== eventKey) : [...prev, eventKey]
    );
  };

  const toggleWebhook = (id: string) => {
    setWebhooks((prev) =>
      prev.map((w) => (w.id === id ? { ...w, enabled: !w.enabled } : w))
    );
  };

  const testWebhook = (id: string) => {
    setShowTestResult({ id, status: 'success' });
    setTimeout(() => setShowTestResult(null), 3000);
  };

  const deleteWebhook = (id: string) => {
    setWebhooks((prev) => prev.filter((w) => w.id !== id));
  };

  const addWebhook = () => {
    if (!newName || !newUrl || newEvents.length === 0) return;
    const newWh: WebhookEndpoint = {
      id: `wh_${Date.now()}`,
      name: newName,
      url: newUrl,
      events: newEvents,
      enabled: true,
      createdAt: new Date().toISOString().split('T')[0],
      lastDelivery: null,
      lastStatus: null,
    };
    setWebhooks((prev) => [newWh, ...prev]);
    setNewName('');
    setNewUrl('');
    setNewEvents([]);
    setShowAddForm(false);
  };

  const tabs = [
    { key: 'webhooks' as const, label: 'Webhooks', icon: 'ri-plug-line' },
    { key: 'zapier' as const, label: 'Zapier', icon: 'ri-flashlight-line' },
    { key: 'n8n' as const, label: 'n8n', icon: 'ri-node-tree' },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Integrações</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Conecte o WZAP Pro com ferramentas externas via webhooks, Zapier e n8n
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-background-100 rounded-full p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === tab.key
                ? 'bg-white text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} text-base`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── ZAPIER ─── */}
      {activeTab === 'zapier' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-flashlight-line text-white text-2xl" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">Zapier Integration</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  Automatize fluxos conectando o WZAP Pro a mais de 5.000 apps via Zapier.
                  Dispare ações quando novos contatos forem criados, mensagens recebidas ou campanhas concluídas.
                </p>
              </div>
            </div>

            <div className="bg-background-50 dark:bg-background-200 rounded-xl p-5 mb-5">
              <h4 className="text-sm font-semibold text-foreground-800 mb-3">Como conectar</h4>
              <div className="space-y-4">
                {[
                  { step: 1, title: 'Crie um Webhook no Zapier', desc: 'No seu painel Zapier, crie um novo Zap e escolha "Webhooks by Zapier" como trigger. Selecione "Catch Hook".' },
                  { step: 2, title: 'Copie a URL do Webhook', desc: 'O Zapier vai gerar uma URL única. Copie essa URL.' },
                  { step: 3, title: 'Configure no WZAP Pro', desc: 'Na aba Webhooks, adicione um novo endpoint com a URL copiada e selecione os eventos que deseja monitorar.' },
                  { step: 4, title: 'Teste e ative', desc: 'Envie um evento de teste e verifique se o Zapier recebeu os dados corretamente. Depois é só ativar seu Zap!' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{s.title}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-primary-50 dark:bg-primary-500/5 rounded-xl border border-primary-200 dark:border-primary-500/10">
              <i className="ri-information-line text-primary-600 text-xl" />
              <div>
                <p className="text-sm font-medium text-primary-800 dark:text-primary-300">Dica</p>
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  Use a aba Webhooks para gerenciar todos os endpoints — tanto do Zapier quanto do n8n e integrações customizadas.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── N8N ─── */}
      {activeTab === 'n8n' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-400 to-rose-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-node-tree text-white text-2xl" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">n8n Integration</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  n8n é uma alternativa open-source ao Zapier. Ideal para quem precisa de fluxos avançados,
                  self-hosted e com total controle dos dados. Conecte via Webhook nodes.
                </p>
              </div>
            </div>

            <div className="bg-background-50 dark:bg-background-200 rounded-xl p-5 mb-5">
              <h4 className="text-sm font-semibold text-foreground-800 mb-3">Como conectar</h4>
              <div className="space-y-4">
                {[
                  { step: 1, title: 'Crie um Webhook Node no n8n', desc: 'No editor do n8n, adicione um nó "Webhook" como trigger do seu workflow. Configure o método como POST.' },
                  { step: 2, title: 'Copie a Production URL', desc: 'Após salvar e ativar o workflow, o n8n vai gerar uma Production URL. Copie essa URL.' },
                  { step: 3, title: 'Configure no WZAP Pro', desc: 'Na aba Webhooks, adicione um novo endpoint com a URL do n8n e selecione os eventos desejados.' },
                  { step: 4, title: 'Processe os dados no n8n', desc: 'Os eventos chegam como JSON no body da requisição. Use Function Nodes para transformar os dados conforme necessário.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{s.title}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-50 dark:bg-background-200 rounded-xl p-5">
              <h4 className="text-sm font-semibold text-foreground-800 mb-3">Exemplo de Payload</h4>
              <pre className="bg-foreground-900 dark:bg-black text-emerald-400 text-xs p-4 rounded-xl overflow-x-auto font-mono leading-relaxed">
{`{
  "event": "message.received",
  "timestamp": "2026-08-04T14:22:00Z",
  "data": {
    "message_id": "msg_abc123",
    "from": "5511999999999",
    "body": "Olá, gostaria de saber mais...",
    "contact": {
      "name": "João Silva",
      "phone": "5511999999999"
    }
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* ─── WEBHOOKS ─── */}
      {activeTab === 'webhooks' && (
        <div className="space-y-5">
          {/* Add button + stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-foreground-600">{webhooks.filter((w) => w.enabled).length} ativos</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full bg-foreground-300" />
                <span className="text-foreground-600">{webhooks.filter((w) => !w.enabled).length} inativos</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line" /> Novo Webhook
            </button>
          </div>

          {/* Add form */}
          {showAddForm && (
            <div className="bg-white dark:bg-background-100 rounded-2xl border border-primary-200 dark:border-primary-500/20 p-5 space-y-4 animate-fadeIn">
              <h4 className="text-sm font-heading font-bold text-foreground-950">Novo Webhook Endpoint</h4>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nome</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ex: CRM — Novo Contato"
                  className="w-full px-3 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">URL do Webhook</label>
                <input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/..."
                  className="w-full px-3 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-2">Eventos</label>
                <div className="flex flex-wrap gap-2">
                  {availableEvents.map((evt) => (
                    <button
                      key={evt.key}
                      type="button"
                      onClick={() => toggleEvent(evt.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                        newEvents.includes(evt.key)
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-50 dark:bg-background-200 text-foreground-600 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300'
                      }`}
                    >
                      <i className={`${evt.icon} text-sm`} />
                      {evt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={addWebhook}
                  disabled={!newName || !newUrl || newEvents.length === 0}
                  className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Adicionar Webhook
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setNewName(''); setNewUrl(''); setNewEvents([]); }}
                  className="px-4 py-2.5 text-sm font-medium text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Webhooks list */}
          <div className="space-y-3">
            {webhooks.length === 0 ? (
              <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-12 text-center">
                <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-plug-line text-2xl text-foreground-400" />
                </div>
                <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300">Nenhum webhook configurado</p>
                <p className="text-xs text-foreground-500 mt-1 mb-5">Crie seu primeiro webhook para começar a integrar</p>
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Criar Webhook
                </button>
              </div>
            ) : (
              webhooks.map((wh) => (
                <div
                  key={wh.id}
                  className={`bg-white dark:bg-background-100 rounded-2xl border p-5 transition-all ${
                    wh.enabled ? 'border-background-200 dark:border-background-300' : 'border-background-100 dark:border-background-300/50 opacity-70'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        wh.enabled ? 'bg-primary-50 dark:bg-primary-500/10' : 'bg-background-100 dark:bg-background-200'
                      }`}>
                        <i className={`ri-plug-line text-lg ${wh.enabled ? 'text-primary-600' : 'text-foreground-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">{wh.name}</h4>
                          {wh.lastStatus && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              wh.lastStatus === 'success'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${wh.lastStatus === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                              Último envio: {wh.lastStatus === 'success' ? 'OK' : 'Falhou'}
                            </span>
                          )}
                        </div>
                        <code className="text-[11px] text-foreground-500 mt-1 block break-all">{wh.url}</code>
                        <p className="text-[10px] text-foreground-400 mt-1">
                          Criado em {new Date(wh.createdAt).toLocaleDateString('pt-BR')}
                          {wh.lastDelivery && ` · Último: ${new Date(wh.lastDelivery).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Test result feedback */}
                      {showTestResult && showTestResult.id === wh.id && (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap animate-fadeIn ${
                          showTestResult.status === 'success'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {showTestResult.status === 'success' ? '✓ Teste OK' : '✗ Falhou'}
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() => testWebhook(wh.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                        title="Testar"
                      >
                        <i className="ri-send-plane-line text-sm" />
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleWebhook(wh.id)}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                          wh.enabled ? 'bg-emerald-500' : 'bg-foreground-300 dark:bg-foreground-600'
                        }`}
                        title={wh.enabled ? 'Desativar' : 'Ativar'}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                          wh.enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                        }`} />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteWebhook(wh.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Excluir"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Event tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {wh.events.map((evt) => {
                      const eventDef = availableEvents.find((e) => e.key === evt);
                      return (
                        <span key={evt} className="inline-flex items-center gap-1 px-2.5 py-1 bg-background-50 dark:bg-background-200 rounded-md text-[10px] font-medium text-foreground-600 dark:text-foreground-400">
                          <i className={`${eventDef?.icon || 'ri-code-line'} text-[10px]`} />
                          {eventDef?.label || evt}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Secret signing info */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-keyhole-line text-amber-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">Assinatura de Webhooks</h4>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                  Cada requisição de webhook inclui o header <code className="bg-background-100 dark:bg-background-200 px-1.5 py-0.5 rounded text-[11px] text-foreground-700">X-WZAP-Signature</code> com HMAC-SHA256.
                  Use o secret abaixo para verificar a autenticidade das requisições no seu servidor.
                </p>
                <div className="flex items-center gap-2">
                  <code className="bg-background-100 dark:bg-background-200 px-3 py-1.5 rounded-lg text-sm font-mono text-foreground-700 select-all">
                    whsec_8a7f3c2e1b4d9f6a5c3e7b2d8f1a4c6e
                  </code>
                  <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    title="Copiar"
                  >
                    <i className="ri-file-copy-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}